/**
 * Button component
 * Reusable button with variants and states
 */

import { type ButtonHTMLAttributes, type ReactNode } from "react";
import styles from "./Button.module.css";

/**
 * Button variants
 */
export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

/**
 * Button sizes
 */
export type ButtonSize = "small" | "medium" | "large";

/**
 * Button component props
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Loading state */
  isLoading?: boolean;
  /** Full width button */
  fullWidth?: boolean;
  /** Button content */
  children: ReactNode;
}

/**
 * Reusable button component with accessibility support
 */
export function Button({
  variant = "primary",
  size = "medium",
  isLoading = false,
  fullWidth = false,
  disabled,
  children,
  className,
  type = "button",
  ...props
}: ButtonProps): React.ReactElement {
  const classNames = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : "",
    isLoading ? styles.loading : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={classNames}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      aria-disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className={styles.spinner} role="status" aria-label="Cargando">
          <span className={styles.spinnerDot} />
        </span>
      )}
      <span className={isLoading ? styles.hiddenContent : ""}>{children}</span>
    </button>
  );
}

export default Button;
